import { useState } from "react";
import {
  Award,
  ExternalLink,
  Edit3,
  Trash2,
  Plus,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { useCurrentUser } from "@/pages/auth/hooks/useAuth";
import { useDeleteCertification } from "./hooks/useCertifications";
import { useCandidateCertifications } from "@/pages/candidate-profile/hooks/useCandidateProfile";
import { CertificationFormModal } from "./components/CertificationFormModal";
import type { CertificationDetail } from "@/interface/certification";

interface CertificationsTabProps {
  profileId?: number | string;
}

export default function CertificationsTab({
  profileId,
}: CertificationsTabProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCert, setSelectedCert] = useState<CertificationDetail | null>(
    null,
  );
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  const { data: user } = useCurrentUser();
  const targetId = profileId || user?.id;

  const {
    data: candCertsRes,
    isLoading: isCandLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCandidateCertifications(targetId, { page: 1, page_size: 10 });
  const deleteMutation = useDeleteCertification();

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 50) {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }
  };

  const certifications =
    candCertsRes?.pages.flatMap((page: any) =>
      Array.isArray(page?.data) ? page.data : page?.data?.results || [],
    ) || [];

  const isLoading = isCandLoading && certifications?.length === 0;

  const handleOpenAdd = () => {
    setSelectedCert(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (cert: CertificationDetail) => {
    setSelectedCert(cert);
    setIsModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTargetId) return;
    try {
      await deleteMutation.mutateAsync(deleteTargetId);
      setDeleteTargetId(null);
    } catch {
      // Error handled by mutation
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
          <Award className="w-5 h-5 text-purple-600 dark:text-purple-400" /> Certifications
        </h2>
        <Button
          onClick={handleOpenAdd}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm font-bold rounded-lg hover:bg-purple-700 transition-colors"
        >
          <Plus className="w-4 h-4" /> Add Certification
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center p-12">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      ) : (
        <div
          onScroll={handleScroll}
          className="grid grid-cols-1 md:grid-cols-2 gap-6 max-h-[700px] overflow-y-auto pr-2 custom-scrollbar"
        >
          {certifications?.map((cert: CertificationDetail) => {
            const certName = cert.certification_name || "";
            const issuer = cert.issuing_organization || "";
            const issueDate = cert.issue_date || "";
            const expiryDate = cert.expiry_date || "";
            const credentialId = cert.credential_id || "";
            const url = cert.credential_url || "";

            return (
              <div
                key={cert.id}
                className="p-5 rounded-xl border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm hover:shadow-md hover:border-purple-200 dark:hover:border-purple-800 transition-all flex flex-col h-full group relative"
              >
                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2 z-10">
                  <button
                    onClick={() => handleOpenEdit(cert)}
                    className="p-1.5 text-slate-400 hover:text-purple-600 dark:hover:text-purple-400 bg-slate-50 dark:bg-slate-800 hover:bg-purple-50 dark:hover:bg-purple-950/40 rounded transition-colors shadow-sm"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => setDeleteTargetId(String(cert.id))}
                    className="p-1.5 text-slate-400 hover:text-red-600 dark:hover:text-red-400 bg-slate-50 dark:bg-slate-800 hover:bg-red-50 dark:hover:bg-red-950/40 rounded transition-colors shadow-sm"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>

                <div className="flex items-start gap-4 mb-4 flex-1">
                  <div className="w-10 h-10 bg-amber-50 dark:bg-amber-950/30 rounded-lg flex items-center justify-center shrink-0 text-amber-500 dark:text-amber-400">
                    <Award className="w-5 h-5" />
                  </div>
                  <div className="pr-16">
                    <h3 className="font-bold text-slate-900 dark:text-white text-[15px] leading-tight mb-1">
                      {certName}
                    </h3>
                    <div className="text-slate-500 dark:text-slate-400 text-sm font-medium">
                      {issuer}
                    </div>
                  </div>
                </div>

                <div className="border-t border-slate-100 dark:border-slate-800 pt-4 mt-auto">
                  <div className="flex justify-between items-center text-xs text-slate-500 dark:text-slate-400 mb-2">
                    <span>
                      Issued:{" "}
                      <span className="font-semibold text-slate-700 dark:text-slate-200">
                        {issueDate || "N/A"}
                      </span>
                    </span>
                    <span>
                      Expires:{" "}
                      <span className="font-semibold text-slate-700 dark:text-slate-200">
                        {expiryDate || "No Expiration"}
                      </span>
                    </span>
                  </div>
                  {credentialId && (
                    <div className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                      Credential ID:{" "}
                      <span className="font-mono bg-slate-50 dark:bg-slate-800 px-1.5 py-0.5 rounded text-slate-700 dark:text-slate-200 border border-slate-200/50 dark:border-slate-700">
                        {credentialId}
                      </span>
                    </div>
                  )}
                  {url && url !== "#" && (
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] font-bold text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 bg-purple-50 dark:bg-purple-950/50 hover:bg-purple-100 dark:hover:bg-purple-900/40 px-2.5 py-1.5 rounded-lg transition-colors"
                    >
                      View Credential <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            );
          })}

          {certifications.length === 0 && (
            <div className="col-span-1 md:col-span-2 text-center p-12 text-slate-500 dark:text-slate-400 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-xl bg-white dark:bg-slate-900">
              No certifications added yet.
            </div>
          )}

          {isFetchingNextPage && (
            <div className="col-span-1 md:col-span-2 flex justify-center items-center py-6">
              <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
              <span className="ml-2 text-sm text-slate-500 dark:text-slate-400 font-medium">
                Loading more certifications...
              </span>
            </div>
          )}
        </div>
      )}

      <CertificationFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        editData={selectedCert}
        profileId={targetId}
      />

      <ConfirmationDialog
        isOpen={Boolean(deleteTargetId)}
        onClose={() => setDeleteTargetId(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Certification"
        description="Are you sure you want to delete this certification?"
        confirmText="Delete"
        confirmVariant="danger"
        isPending={deleteMutation.isPending}
      />
    </div>
  );
}
